import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const SubscriptionManager = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [daysToExtend, setDaysToExtend] = useState(7);
  const [durationDays, setDurationDays] = useState(30);
  const [notifyUser, setNotifyUser] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });

  const functions = getFunctions();

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, pagination.page]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const getAllSubscriptionsFunction = httpsCallable(functions, 'getAllUserSubscriptions');
      const result = await getAllSubscriptionsFunction({
        searchQuery,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });

      setUsers(result.data.users);
      setPagination(result.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const getUserDetailsFunction = httpsCallable(functions, 'getUserSubscriptionDetails');
      const result = await getUserDetailsFunction({ userId });
      setSelectedUser(result.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const handleExtendSubscription = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    try {
      // Use the legacy function for free subscriptions (from the modal)
      const extendFunction = httpsCallable(functions, 'extendFreeSubscription');
      const result = await extendFunction({
        userId: selectedUser.user.userId,
        daysToExtend: parseInt(daysToExtend),
        notifyUser,
      });

      toast.success(result.data.message);
      setShowExtendModal(false);
      setShowDetailsModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error extending subscription:', error);
      toast.error(error.message || 'Failed to extend subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateFreeSubscription = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    try {
      const createFunction = httpsCallable(functions, 'createFreeSubscription');
      const result = await createFunction({
        userId: selectedUser.user.userId,
        durationDays: parseInt(durationDays),
        notifyUser,
      });

      toast.success(result.data.message);
      setShowCreateModal(false);
      setShowDetailsModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.message || 'Failed to create subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!selectedUser || !window.confirm('Are you sure you want to delete this transaction?')) return;

    setIsLoading(true);
    try {
      const deleteFunction = httpsCallable(functions, 'removeSubscriptionTransaction');
      const result = await deleteFunction({
        userId: selectedUser.user.userId,
        transactionId,
      });

      toast.success(result.data.message);
      fetchUserDetails(selectedUser.user.userId);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error(error.message || 'Failed to delete transaction');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAllSubscriptions = async () => {
    if (!selectedUser || !window.confirm('⚠️ WARNING: This will DELETE ALL subscription data for this user. This action CANNOT be undone. Are you absolutely sure?')) return;

    setIsLoading(true);
    try {
      const deleteFunction = httpsCallable(functions, 'deleteUserSubscription');
      const result = await deleteFunction({
        userId: selectedUser.user.userId,
      });

      toast.success(result.data.message);
      setShowDetailsModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error(error.message || 'Failed to delete subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFreeSubscriptions = async () => {
    if (!selectedUser || !window.confirm('This will remove all FREE subscription entries from this user, keeping only paid ones. Continue?')) return;

    setIsLoading(true);
    try {
      const removeFunction = httpsCallable(functions, 'removeFreeSubscriptions');
      const result = await removeFunction({
        userId: selectedUser.user.userId,
      });

      toast.success(result.data.message);
      fetchUserDetails(selectedUser.user.userId);
      fetchUsers();
    } catch (error) {
      console.error('Error removing free subscriptions:', error);
      toast.error(error.message || 'Failed to remove free subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExtendAnySubscription = async (transactionId) => {
    const days = prompt('How many days would you like to extend this subscription?', '7');
    if (!days || isNaN(days) || days <= 0) {
      toast.error('Please enter a valid number of days');
      return;
    }

    setIsLoading(true);
    try {
      const extendFunction = httpsCallable(functions, 'extendUserSubscription');
      const result = await extendFunction({
        userId: selectedUser.user.userId,
        daysToExtend: parseInt(days),
        transactionId,
        notifyUser: true,
      });

      toast.success(result.data.message);
      fetchUserDetails(selectedUser.user.userId);
      fetchUsers();
    } catch (error) {
      console.error('Error extending subscription:', error);
      toast.error(error.message || 'Failed to extend subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpireSubscription = async (transactionId) => {
    if (!selectedUser || !window.confirm('Are you sure you want to IMMEDIATELY expire this subscription? This action will set the end date to now.')) return;

    setIsLoading(true);
    try {
      const expireFunction = httpsCallable(functions, 'expireUserSubscription');
      const result = await expireFunction({
        userId: selectedUser.user.userId,
        transactionId,
        notifyUser: true,
      });

      toast.success(result.data.message);
      fetchUserDetails(selectedUser.user.userId);
      fetchUsers();
    } catch (error) {
      console.error('Error expiring subscription:', error);
      toast.error(error.message || 'Failed to expire subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      none: 'bg-gray-100 text-gray-800',
    };
    return badges[status] || badges.none;
  };

  const getTypeBadge = (type) => {
    const badges = {
      free: 'bg-blue-100 text-blue-800',
      paid: 'bg-purple-100 text-purple-800',
      failed: 'bg-red-100 text-red-800',
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadgeForTransaction = (status) => {
    const badges = {
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-orange-100 text-orange-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getRemainingDays = (endDate) => {
    if (!endDate) return 0;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-900">Subscription Management</h3>
        <div className="relative w-96">
          <input
            type="text"
            placeholder="Search by name, email, phone, or user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <svg
            className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Users Table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : users.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                      <div className="text-xs text-gray-400">{user.mobileNumber}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{user.userCategory}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(user.subscription.type)}`}>
                        {user.subscription.currentPlan}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(user.subscription.status)}`}>
                        {user.subscription.status}
                        {user.subscription.status === 'active' && ` (${getRemainingDays(user.subscription.endDate)}d)`}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.subscription.endDate)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{user.subscription.platform}</td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <button
                        onClick={() => fetchUserDetails(user.userId)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">No users found</p>
        </div>
      )}

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
            {/* Modal Header */}
            <div className="border-b border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedUser.user.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedUser.user.email}</p>
                  <p className="text-xs text-gray-500">{selectedUser.user.mobileNumber}</p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <label className="text-xs font-medium text-gray-500">User Category</label>
                  <p className="text-sm font-semibold text-gray-900">{selectedUser.user.userCategory}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Joined On</label>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(selectedUser.user.createdAt)}</p>
                </div>
              </div>

              {/* Action Buttons */}
              {selectedUser.subscription.exists && (
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={handleRemoveFreeSubscriptions}
                    disabled={isLoading}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 text-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove All Free Subscriptions
                  </button>
                  <button
                    onClick={handleDeleteAllSubscriptions}
                    disabled={isLoading}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Delete All Subscription Data
                  </button>
                </div>
              )}

              {/* Current Subscription */}
              {selectedUser.subscription.exists && selectedUser.subscription.active ? (
                <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-green-900">Active Subscription</h4>
                      <p className="text-sm text-green-700 mt-2">
                        <strong>Plan:</strong> {selectedUser.subscription.active.category || selectedUser.subscription.active.type}
                      </p>
                      <p className="text-sm text-green-700">
                        <strong>Type:</strong> <span className="capitalize">{selectedUser.subscription.active.type}</span>
                      </p>
                      <p className="text-sm text-green-700">
                        <strong>Valid Until:</strong> {formatDate(selectedUser.subscription.active.endTime)}
                      </p>
                      <p className="text-sm text-green-700">
                        <strong>Days Remaining:</strong> {getRemainingDays(selectedUser.subscription.active.endTime)} days
                      </p>
                    </div>
                    {selectedUser.subscription.active.type === 'free' && (
                      <button
                        onClick={() => setShowExtendModal(true)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                      >
                        Extend
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
                  <h4 className="font-bold text-red-900">No Active Subscription</h4>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Grant Free Subscription
                  </button>
                </div>
              )}

              {/* Transaction History */}
              <div>
                <h4 className="font-bold text-gray-900 mb-4">
                  Transaction History ({selectedUser.subscription.transactions?.length || 0})
                  {selectedUser.subscription.failedTransactionsCount > 0 && (
                    <span className="ml-2 text-sm font-normal text-red-600">
                      ({selectedUser.subscription.failedTransactionsCount} failed)
                    </span>
                  )}
                </h4>
                {selectedUser.subscription.transactions && selectedUser.subscription.transactions.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedUser.subscription.transactions.map((transaction, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                          transaction.status === 'failed' || transaction.status === 'cancelled'
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadge(transaction.type)}`}>
                                {transaction.type}
                              </span>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeForTransaction(transaction.status)}`}>
                                {transaction.status}
                              </span>
                              <span className="text-sm font-semibold text-gray-900">
                                {transaction.category || 'N/A'}
                              </span>
                              {/* Admin Action Badges */}
                              {transaction.expiredBy === 'admin' && (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Expired by Admin
                                </span>
                              )}
                              {/* Check if subscription expired automatically (end date passed, not expired by admin) */}
                              {!transaction.expiredBy && transaction.endTime && new Date(transaction.endTime) < new Date() && transaction.status === 'paid' && (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  Expired Automatically
                                </span>
                              )}
                              {transaction.extendedBy === 'admin' && transaction.extendedDays && (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                  Extended by Admin (+{transaction.extendedDays}d)
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                              <div>
                                <strong>{transaction.status === 'failed' || transaction.status === 'cancelled' ? 'Time:' : 'Start:'}</strong> {formatDateTime(transaction.startTime)}
                              </div>
                              {transaction.status !== 'failed' && transaction.status !== 'cancelled' && (
                                <div>
                                  <strong>End:</strong> {formatDateTime(transaction.endTime)}
                                </div>
                              )}
                              <div>
                                <strong>Amount:</strong> ₹{transaction.amount}
                              </div>
                              <div>
                                <strong>Platform:</strong> {transaction.platform}
                              </div>
                              {transaction.transactionId && (
                                <div className="col-span-2">
                                  <strong>Transaction ID:</strong> <span className="font-mono text-xs">{transaction.transactionId}</span>
                                </div>
                              )}
                              {transaction.phoneNo && (
                                <div className="col-span-2">
                                  <strong>Phone:</strong> {transaction.phoneNo}
                                </div>
                              )}
                              {transaction.userName && (
                                <div className="col-span-2">
                                  <strong>User Name:</strong> {transaction.userName}
                                </div>
                              )}
                              {/* Admin Action Details */}
                              {transaction.expiredBy === 'admin' && (
                                <>
                                  <div className="col-span-2 mt-2 pt-2 border-t border-orange-200">
                                    <span className="text-orange-800 font-semibold">⏰ Admin Expiration Details:</span>
                                  </div>
                                  <div>
                                    <strong>Expired At:</strong> {formatDateTime(transaction.expiredAt)}
                                  </div>
                                  {transaction.previousEndDate && (
                                    <div>
                                      <strong>Original End Date:</strong> {formatDateTime(transaction.previousEndDate)}
                                    </div>
                                  )}
                                </>
                              )}
                              {transaction.extendedBy === 'admin' && (
                                <>
                                  <div className="col-span-2 mt-2 pt-2 border-t border-indigo-200">
                                    <span className="text-indigo-800 font-semibold">➕ Admin Extension Details:</span>
                                  </div>
                                  <div>
                                    <strong>Days Extended:</strong> {transaction.extendedDays} days
                                  </div>
                                  {transaction.lastExtendedAt && (
                                    <div>
                                      <strong>Extended At:</strong> {formatDateTime(transaction.lastExtendedAt)}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          {/* Only show action buttons for successful transactions */}
                          {transaction.status !== 'failed' && transaction.status !== 'cancelled' && (
                            <div className="ml-4 flex flex-col gap-2">
                              <button
                                onClick={() => handleExtendAnySubscription(transaction.transactionId)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                title="Extend this subscription"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleExpireSubscription(transaction.transactionId)}
                                className="text-orange-600 hover:text-orange-800 text-sm"
                                title="Expire this subscription now"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              {transaction.type === 'free' && transaction.transactionId && (
                                <button
                                  onClick={() => handleDeleteTransaction(transaction.transactionId)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                  title="Delete transaction"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                          {/* Show failed indicator for failed transactions */}
                          {(transaction.status === 'failed' || transaction.status === 'cancelled') && (
                            <div className="ml-4">
                              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No transaction history</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Extend Free Subscription</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Days to Extend</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={daysToExtend}
                  onChange={(e) => setDaysToExtend(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={notifyUser}
                  onChange={(e) => setNotifyUser(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Send notification to user</label>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowExtendModal(false)}
                  className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendSubscription}
                  disabled={isLoading}
                  className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isLoading ? 'Extending...' : 'Extend'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Free Subscription Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Grant Free Subscription</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (Days)</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={notifyUser}
                  onChange={(e) => setNotifyUser(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Send notification to user</label>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFreeSubscription}
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;
